import type { ErrorResponse } from '@/api/handlers/responses';

/**
 * Turning a bos-api refusal into something a seller can read (SEL-19).
 *
 * Two rules, inherited from BosApp's error model:
 *
 * 1. **Branch on `error.code`, never on the message text.** The message is
 *    prose written for a human, in whatever language the server happens to
 *    speak; the code is the contract.
 * 2. **An unmapped code is not swallowed.** It falls through to the server's
 *    own message, which is more useful than a generic "something went wrong"
 *    and makes a new contract code visible instead of invisible.
 *
 * The last resort is a localized text for the HTTP status, so a toast is never
 * empty even when the server sends neither a code nor a message.
 */

/** i18n keys are fully qualified so a `pages`-scoped `t` can resolve them. */
const NS = 'components:apiErrors';

/**
 * The branch this terminal is bound to has been archived. Named here, with the
 * other codes, so the screens that give it page-specific copy — enrollment and
 * the seller picker — don't each carry their own string literal.
 */
export const BRANCH_ARCHIVED = 'BRANCH_ARCHIVED';

/**
 * Five wrong PINs locked the account for a few minutes: PIN login, PIN verify
 * or a shift co-sign (BOS-190). The PIN screens tell it from a wrong PIN by this
 * code alone.
 */
export const PIN_LOCKED = 'PIN_LOCKED';

/**
 * A sign-in rate limit: a 429 whose `Retry-After` says how long to wait
 * (BOS-190).
 */
export const RATE_LIMITED = 'RATE_LIMITED';

/**
 * A refresh refused because the session was ended on the server (BOS-161): the
 * seller signed in on another sale point, or their access was removed. The
 * device goes back to the seller picker and says why.
 */
export const SESSION_REVOKED = 'SESSION_REVOKED';

/**
 * A close or open whose count differs from the server's expected cash arrived
 * without a co-sign (BOS-167). The shift flow moves to its approver step on
 * this code, whatever its own arithmetic said.
 */
export const APPROVAL_REQUIRED = 'APPROVAL_REQUIRED';

/**
 * The co-sign was refused: the approver is not a different, active BranchAdmin
 * of this branch, or the PIN is wrong (BOS-167).
 */
export const APPROVER_INVALID = 'APPROVER_INVALID';

/**
 * The shift this device remembers is gone from the caller's branch (BOS-168).
 * The sale point handover guard forgets it on this code alone; a bare 404 is a
 * route the server doesn't have, and must not switch the guard off.
 */
export const SHIFT_NOT_FOUND = 'SHIFT_NOT_FOUND';

/**
 * A BranchAdmin's close by id found the shift already closed (BOS-169): its
 * seller or another admin closed it first. Nothing was written.
 */
export const SHIFT_ALREADY_CLOSED = 'SHIFT_ALREADY_CLOSED';

/**
 * Codes the seller app speaks. The ledger-posting refusals are grouped: a
 * cashier can't act on the difference between a missing settlement account and
 * an archived one — both mean "the books aren't set up, fetch the manager".
 */
const MESSAGE_KEYS: Record<string, string> = {
    PRODUCT_ARCHIVED: `${NS}.productArchived`,
    // A stale cart line on a product an import made a draft: not for sale yet.
    PRODUCT_DRAFT: `${NS}.productDraft`,
    // The branch can't fill a cart line from its sellable stock: other
    // branches' and expired stock don't count (BOS-162).
    INSUFFICIENT_STOCK: `${NS}.insufficientStock`,
    [BRANCH_ARCHIVED]: `${NS}.branchArchived`,
    INTERNAL_ERROR: `${NS}.internalError`,
    [PIN_LOCKED]: `${NS}.pinLocked`,
    // The seller has no open shift of their own, or already holds one: shifts
    // belong to the seller, so either can change on another sale point
    // (BOS-166).
    NO_OPEN_SHIFT: `${NS}.noOpenShift`,
    SHIFT_ALREADY_OPEN: `${NS}.shiftAlreadyOpen`,
    [RATE_LIMITED]: `${NS}.status.rateLimited`,
    // The shift co-sign (BOS-167).
    [APPROVAL_REQUIRED]: `${NS}.approvalRequired`,
    [APPROVER_INVALID]: `${NS}.approverInvalid`,
    // An admin closing a stuck shift by id (BOS-169): someone closed it first,
    // or it is no longer in the admin's branch.
    [SHIFT_ALREADY_CLOSED]: `${NS}.shiftAlreadyClosed`,
    [SHIFT_NOT_FOUND]: `${NS}.shiftNotFound`,
    // The close difference couldn't be booked, so the shift stays open. Its own
    // code rather than the ledger groups below: the seller has to know the
    // close did not happen, not only that the books refused something.
    LEDGER_POSTING_REFUSED: `${NS}.ledgerPostingRefused`,

    // A sale's discounts (API-159). The till checks the same rules before it
    // posts, so these mean the cart and the server disagree: a malformed
    // value, one that no longer fits, or the same variant on two lines.
    DISCOUNT_INVALID: `${NS}.discountInvalid`,
    DISCOUNT_TOO_LARGE: `${NS}.discountTooLarge`,
    DISCOUNT_LINE_AMBIGUOUS: `${NS}.discountLineAmbiguous`,
    // The payments don't sum to the server's total: a price moved under the
    // cart, or a discount was charged differently. It reached the seller as
    // the server's English until the code existed.
    PAYMENT_MISMATCH: `${NS}.paymentMismatch`,

    // Ledger posting refusals — the books can't take the entry.
    ACCOUNT_NOT_FOUND: `${NS}.ledgerAccounts`,
    ACCOUNT_ARCHIVED: `${NS}.ledgerAccounts`,
    ACCOUNT_NOT_POSTABLE: `${NS}.ledgerAccounts`,
    SETTLEMENT_ACCOUNT_MISSING: `${NS}.ledgerAccounts`,
    SETTLEMENT_ACCOUNT_AMBIGUOUS: `${NS}.ledgerAccounts`,
    PERIOD_CLOSED: `${NS}.ledgerPeriod`,
    PERIOD_NOT_DEFINED: `${NS}.ledgerPeriod`,
    PERIOD_LOCKED: `${NS}.ledgerPeriod`,
    NO_RATES_IN_FORCE: `${NS}.ledgerRates`,
    AMOUNT_NEGATIVE: `${NS}.ledgerAmount`,
    AMOUNT_PRECISION: `${NS}.ledgerAmount`,
    ENTRY_NUMBER_COLLISION: `${NS}.ledgerRetry`,
};

const STATUS_KEYS: Record<number, string> = {
    400: `${NS}.status.badRequest`,
    401: `${NS}.status.unauthorized`,
    403: `${NS}.status.forbidden`,
    404: `${NS}.status.notFound`,
    409: `${NS}.status.conflict`,
    429: `${NS}.status.rateLimited`,
};

export type ResolvedApiError = {
    /** The server's code, when it sent one. */
    code?: string;
    /** Key for a code we know, or `null` — the caller then uses the fallbacks. */
    messageKey: string | null;
    /** Key for the HTTP status. Never null: this is what stops an empty toast. */
    statusKey: string;
    /** The server's own prose, or `''` when it sent none (or we can't trust it). */
    fallbackText: string;
    /**
     * Shown with the message so a user's report leads to the log entry. Set
     * only for a server error that carried one.
     */
    correlationId: string | null;
};

const isServerError = (status: number) => status >= 500;

export const resolveApiError = (
    error: ErrorResponse | null | undefined,
): ResolvedApiError => {
    if (!error) {
        return {
            messageKey: null,
            statusKey: `${NS}.status.unknown`,
            fallbackText: '',
            correlationId: null,
        };
    }

    const status = error.status;
    const code = error.code;

    // `isAxiosError` marks an envelope the client synthesized (a cancel, a
    // network failure) — its `message` is a developer string, not server prose,
    // so it never reaches a seller.
    const fallbackText = error.isAxiosError
        ? ''
        : (error.message?.trim() ?? '');

    const statusKey = error.isAxiosError
        ? status === 499
            ? `${NS}.status.cancelled`
            : `${NS}.status.network`
        : (STATUS_KEYS[status] ??
          (isServerError(status)
              ? `${NS}.status.serverError`
              : `${NS}.status.unknown`));

    return {
        ...(code ? { code } : {}),
        messageKey: (code ? MESSAGE_KEYS[code] : undefined) ?? null,
        statusKey,
        fallbackText,
        correlationId:
            isServerError(status) && error.correlationId
                ? error.correlationId
                : null,
    };
};

/** The `t` a caller already holds, narrowed to what this module needs. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The message to show a seller for `error`: a localized text for a code we
 * know, else the server's own words, else a localized text for the status. A
 * server error carries its correlation id along so the report can be traced.
 */
export const apiErrorMessage = (
    error: ErrorResponse | null | undefined,
    t: Translate,
): string => {
    const resolved = resolveApiError(error);
    const message = resolved.messageKey
        ? t(resolved.messageKey)
        : resolved.fallbackText || t(resolved.statusKey);

    return resolved.correlationId
        ? t(`${NS}.withReference`, {
              message,
              reference: resolved.correlationId,
          })
        : message;
};
